import {html, TemplateResult} from "lit";
import {StyleInfo, styleMap} from "lit/directives/style-map.js";
import {getReadableTextShade} from "@o2d/shared";

// Calibrated so the L* decision matches actual WCAG contrast parity for the
// app's dark-text token, --ha-color-dark: #272727 (theme-default.css,
// Anke's value — CSS ownership is hers, this constant is kept in sync with
// it, not the other way around). @o2d/shared's textbook default (50) assumes
// a symmetric black/white choice; #272727 is close to but not quite black,
// so the real break-even sits slightly higher (~54.2). Recalibrate here, not
// in the package, if theme-default.css's --ha-color-dark ever changes.
const LIGHTNESS_THRESHOLD_FOR_ROW_HEADER = 54;

// Warns at most once per distinct unparseable color value — this runs per
// visible row on every render of the virtualized timeline grid (e.g. on
// scroll), so an unguarded console.warn would flood the console for a single
// bad data point.
const warnedInvalidColors = new Set<string>();

/** The two tokens the automatic text colour chooses between. */
type ReadableTextColorToken = '--ha-color-dark' | '--ha-color-light';

/**
 * Which token gives readable contrast against a hex colour — or undefined when the value is not a parseable hex
 * colour (warned once per value). `#rgb`, `#rrggbb` and the alpha forms are understood by `getReadableTextShade`.
 */
function readableTextColorToken(color: string): ReadableTextColorToken | undefined {
    try {
        return getReadableTextShade(color, LIGHTNESS_THRESHOLD_FOR_ROW_HEADER) === 'dark' ? '--ha-color-dark' : '--ha-color-light';
    } catch (error) {
        if (!warnedInvalidColors.has(color)) {
            warnedInvalidColors.add(color);
            console.warn(`[buildHeaderItemStyleMap] Unparseable color "${color}"`, error);
        }
        return undefined;
    }
}

/** A text colour a user set: exactly `#rrggbb` (as the backend stores it). */
const FIXED_TEXT_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * The text colour on top of a record colour (feature plan 0074), as a CSS value — in this order:
 *
 * 1. No record colour (`null`), one that is no readable hex colour, or a CSS reference (the legacy grid's
 *    `var(--row__header-background-color)`) → `undefined`, whatever `textColor` says: the text colour only applies
 *    where the record colour is the background.
 * 2. A valid set `textColor` wins.
 * 3. Otherwise "automatisch": `var(--ha-color-dark)` or `var(--ha-color-light)` against `color` (threshold 54).
 */
export function resolveRecordTextColor(color: string | null | undefined, textColor: string | null | undefined = null): string | undefined {
    // `undefined` too: entities built without a colour (fixtures, half-loaded references) must not break a render.
    if (typeof color !== `string` || color.startsWith(`var(`)) {
        return undefined;
    }
    const token = readableTextColorToken(color);
    if (token === undefined) {
        return undefined;
    }
    if (typeof textColor === `string` && FIXED_TEXT_COLOR.test(textColor)) {
        return textColor;
    }
    return `var(${token})`;
}

/**
 * The default values of the two tokens (`theme-default.css:9-10`), kept in sync with it like the threshold above.
 * Used only as the starting value of the text colour field when "Automatisch" is switched off: a tenant theme with
 * other tones moves that starting value at most a little, and the user then picks freely (feature plan 0074).
 */
const TOKEN_DEFAULT_HEX: Record<ReadableTextColorToken, string> = {
    '--ha-color-dark': '#272727',
    '--ha-color-light': '#ffffff',
};

/** The automatic text colour against `color` as `#rrggbb` — `#272727` when `color` is missing or unreadable. */
export function autoTextColorHex(color: string | null): string {
    const token = color === null ? undefined : readableTextColorToken(color);
    return TOKEN_DEFAULT_HEX[token ?? '--ha-color-dark'];
}

/**
 * Used by the Board/Timeline row headers and, since feature plan 0072, by the picker gimmick's wheel segments,
 * drum balls and turned card (`components/gimmicks/picker/pool-color.ts`) — recalibrating the threshold above
 * changes the shortcut contrast there too.
 *
 * Builds the `--o2d-color-1` custom property for a row-header cell
 * (`HeaderItem.color`, sourced from the entity's own `Colorizable` color —
 * employee, project, etc.) so `timeline.css` can style the first Board/Timeline
 * column based on the entity's assigned color. Also sets `--text-color` to a
 * readable light/dark contrast choice against that same color — or the record's own text colour, feature plan
 * 0074, see `resolveRecordTextColor` — (consumed via
 * `color: var(--text-color)` — the same mechanism the CSS itself already
 * uses, see timeline.css). Returns an empty style object
 * when the entity has no color, leaving the cell unstyled.
 */
export function buildHeaderItemStyleMap(color: string | null, textColor: string | null = null): StyleInfo {
    if (color === null) {
        return {};
    }

    const style: StyleInfo = {'--o2d-color-1': color};
    const resolved = resolveRecordTextColor(color, textColor);
    if (resolved !== undefined) {
        style['--text-color'] = resolved;
    }

    return style;
}

/**
 * `--item-color` and its text colour `--item-text-color` for a chip painted in a record's colour — the status chips
 * of task list and My Organizer, combobox values (feature plan 0074). ralphy.css reads `--item-text-color` with
 * today's fixed colour as fallback.
 */
export function itemColorStyle(color: string | null, textColor: string | null = null): StyleInfo {
    // `initial` rather than leaving it out: custom properties inherit, and a chip without a colour of its own must
    // not take the text colour of an enclosing one (e.g. the selected value around a popover) — `initial` makes
    // `var(--item-text-color, <fallback>)` use its fallback.
    return {'--item-color': color ?? undefined, '--item-text-color': resolveRecordTextColor(color, textColor) ?? 'initial'};
}

/**
 * The sample of a colour combination in the edit dialogs (feature plan 0074): the record's name on its colour, in
 * its text colour. `.color-sample` in ralphy.css paints `--o2d-color-1` / `--text-color`.
 */
export function renderColorSample(color: string | null, textColor: string | null, text: string): TemplateResult {
    return html`<span class="color-sample" style=${styleMap(buildHeaderItemStyleMap(color, textColor))}>${text}</span>`;
}
